import { createContext, useContext, useEffect, useState } from "react";
import { EMPTY_MEASUREMENTS } from "../lib/specs";

const MeasurementsContext = createContext(null);
export const useMeasurements = () => useContext(MeasurementsContext);

const LS_M = "h2-measurements";
const LS_P = "h2-photos";

const load = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

// corner -> { wheelKey, axle } mapping
const CORNER = {
  FL: { wheel: "frontLeft", axle: "front" },
  FR: { wheel: "frontRight", axle: "front" },
  RL: { wheel: "rearLeft", axle: "rear" },
  RR: { wheel: "rearRight", axle: "rear" },
};

export function MeasurementsProvider({ children }) {
  const [m, setM] = useState(() => load(LS_M, EMPTY_MEASUREMENTS));
  const [photos, setPhotos] = useState(() => load(LS_P, {}));

  useEffect(() => { localStorage.setItem(LS_M, JSON.stringify(m)); }, [m]);
  useEffect(() => { localStorage.setItem(LS_P, JSON.stringify(photos)); }, [photos]);

  const setWheel = (wheel, field, val) =>
    setM((p) => ({ ...p, [wheel]: { ...p[wheel], [field]: val } }));
  const setFlat = (field, val) => setM((p) => ({ ...p, [field]: val }));

  const loadFactory = (specs) => {
    if (!specs) return;
    const t = (s) => s.target.toString();
    setM({
      frontLeft: { camber: t(specs.front.camber), caster: t(specs.front.caster) },
      frontRight: { camber: t(specs.front.camber), caster: t(specs.front.caster) },
      rearLeft: { camber: t(specs.rear.camber) },
      rearRight: { camber: t(specs.rear.camber) },
      frontToe: t(specs.front.totalToe),
      rearToe: t(specs.rear.totalToe),
    });
  };

  const reset = () => { setM(EMPTY_MEASUREMENTS); setPhotos({}); };

  // Apply a scanned angle to a specific corner.
  const setCornerAngle = (corner, type, value) => {
    const map = CORNER[corner];
    if (!map) return;
    const v = typeof value === "number" ? value.toFixed(2) : String(value);
    if (type === "toe") {
      setFlat(map.axle === "front" ? "frontToe" : "rearToe", v);
    } else {
      // camber or caster
      setWheel(map.wheel, type, v);
    }
  };

  const setPhoto = (corner, photo) => setPhotos((p) => ({ ...p, [corner]: photo }));
  const clearPhoto = (corner) => setPhotos((p) => { const n = { ...p }; delete n[corner]; return n; });

  const value = { m, setM, setWheel, setFlat, loadFactory, reset, setCornerAngle, photos, setPhoto, clearPhoto };
  return <MeasurementsContext.Provider value={value}>{children}</MeasurementsContext.Provider>;
}
